import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import clamp from 'lodash/clamp';
import useIsMobile from 'hooks/use-is-mobile';
import { useFeatureFlag } from 'hooks/use-feature-flags';
import { FeatureFlag } from 'constants/feature-flags';
import { NAVBAR_WIDTH } from 'constants/themes.constants';

export const NAVBAR_MIN_WIDTH = parseInt(NAVBAR_WIDTH, 10);
export const NAVBAR_MAX_WIDTH = 480;

interface NavbarCollapseContextType {
  isCollapsed: boolean;
  toggleCollapsed: () => void;
  isResizable: boolean;
  expandedWidth: number;
  setExpandedWidth: (width: number, options?: { persist?: boolean }) => void;
}

const NavbarCollapseContext = createContext<NavbarCollapseContextType | null>(null);

const COLLAPSE_STORAGE_KEY = 'navbar-collapsed';
const WIDTH_STORAGE_KEY = 'navbar-width';

const clampWidth = (width: number) => clamp(Math.round(width), NAVBAR_MIN_WIDTH, NAVBAR_MAX_WIDTH);

const readStoredWidth = () => {
  const stored = Number(localStorage.getItem(WIDTH_STORAGE_KEY));
  return stored ? clampWidth(stored) : NAVBAR_MIN_WIDTH;
};

interface NavbarCollapseProviderProps {
  children: ReactNode;
}

export const NavbarCollapseProvider = ({ children }: NavbarCollapseProviderProps) => {
  const { isDesktop } = useIsMobile();
  const isResizeEnabled = useFeatureFlag(FeatureFlag.PROJECT_SIDEBAR_TREE);

  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    return stored === 'true';
  });
  const [storedWidth, setStoredWidth] = useState(readStoredWidth);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, isCollapsed.toString());
  }, [isCollapsed]);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const setExpandedWidth = useCallback((width: number, { persist = true } = {}) => {
    const clampedWidth = clampWidth(width);
    setStoredWidth(clampedWidth);
    if (persist) {
      localStorage.setItem(WIDTH_STORAGE_KEY, clampedWidth.toString());
    }
  }, []);

  const effectiveIsCollapsed = isDesktop ? isCollapsed : false;
  const isResizable = isResizeEnabled && isDesktop;

  return (
    <NavbarCollapseContext.Provider
      value={{
        isCollapsed: effectiveIsCollapsed,
        toggleCollapsed,
        isResizable,
        expandedWidth: isResizable ? storedWidth : NAVBAR_MIN_WIDTH,
        setExpandedWidth
      }}>
      {children}
    </NavbarCollapseContext.Provider>
  );
};

export const useNavbarCollapse = (): NavbarCollapseContextType => {
  const context = useContext(NavbarCollapseContext);
  if (!context) {
    throw new Error('useNavbarCollapse must be used within a NavbarCollapseProvider');
  }
  return context;
};
